/**
 * What a programme's history filter holds, and the audit query it asks for.
 *
 * @module components/history/programme-history
 */

import {
  actionsInCategories,
  type AuditEventCategory,
} from "@/components/shared/audit-event-categories";
import type { AuditQuery } from "@/platform";
import type { AuditEvent } from "@/server/types";
import { formatDate } from "@/utils/format-instant";

/** The categories a programme's history offers, in the order the filter lists them. */
export const HISTORY_CATEGORIES: readonly AuditEventCategory[] = [
  "revision",
  "workflow",
  "review",
  "approval",
  "export",
  "publication",
  "team",
  "module",
  "editing",
];

/**
 * What "every kind" shows. Editing sessions are left out: a lease is taken on
 * every open and would bury the decisions around it. They stay one pick away.
 */
const DEFAULT_CATEGORIES = HISTORY_CATEGORIES.filter((category) => category !== "editing");

export interface HistoryFilterValues {
  /** A category, or empty for every kind but editing sessions. */
  category: AuditEventCategory | "";
  actor: string;
  /** `YYYY-MM-DD`, the first local day shown. */
  from: string;
  /** `YYYY-MM-DD`, the last local day shown. */
  to: string;
}

export const NO_HISTORY_FILTERS: HistoryFilterValues = {
  category: "",
  actor: "",
  from: "",
  to: "",
};

/** Midnight at the start of a local calendar day, as an ISO instant. */
function localDayStart(day: string, offsetDays = 0): string {
  const [year, month, date] = day.split("-").map(Number);
  return new Date(year ?? 0, (month ?? 1) - 1, (date ?? 1) + offsetDays).toISOString();
}

export function historyRangeBackwards(values: HistoryFilterValues): boolean {
  return Boolean(values.from && values.to && values.from > values.to);
}

/**
 * The server query for one programme's history. `to` covers its whole day; a
 * backwards range sends no dates rather than a query the server refuses.
 */
export function historyQueryFor(
  programmeId: string,
  values: HistoryFilterValues,
): Omit<AuditQuery, "limit"> {
  const dated = !historyRangeBackwards(values);
  return {
    programmeId,
    actions: actionsInCategories(values.category ? [values.category] : DEFAULT_CATEGORIES),
    ...(values.actor ? { actor: values.actor } : {}),
    ...(dated && values.from ? { since: localDayStart(values.from) } : {}),
    ...(dated && values.to ? { until: localDayStart(values.to, 1) } : {}),
  };
}

export function historyFiltersActive(values: HistoryFilterValues): boolean {
  return Object.values(values).some(Boolean);
}

export interface HistoryDay {
  /** The local calendar day, as the tenant's region writes it. */
  day: string;
  events: AuditEvent[];
}

/** Newest-first events, split at each local day they cross. */
export function groupByDay(events: readonly AuditEvent[]): HistoryDay[] {
  return events.reduce<HistoryDay[]>((days, event) => {
    const day = formatDate(event.timestamp);
    const last = days.at(-1);
    if (last?.day === day) {
      last.events.push(event);
    } else {
      days.push({ day, events: [event] });
    }
    return days;
  }, []);
}

function text(event: AuditEvent, key: string): string | undefined {
  const value = event.metadata?.[key];
  if (typeof value === "number") {
    return String(value);
  }
  return typeof value === "string" && value.trim() ? value : undefined;
}

/**
 * The one fact that says what an event changed, where its metadata carries it:
 * the states a transition moved between, the revision an approval made, the
 * format of an export, why a publication was withdrawn.
 */
export function historyEventDetail(event: AuditEvent): string | undefined {
  const from = text(event, "fromState") ?? text(event, "fromStatus");
  const to = text(event, "toState") ?? text(event, "toStatus");
  if (to) {
    return from ? `${from} → ${to}` : `To ${to}`;
  }
  // A status set to null was cleared, as a return to Draft clears delivery.
  if (from && event.metadata?.toStatus === null) {
    return `${from} → none`;
  }
  const revision = text(event, "curriculumRevisionNumber") ?? text(event, "revisionNumber");
  if (revision) {
    return `Revision ${revision}`;
  }
  const format = text(event, "format");
  if (format) {
    return `${format.toUpperCase()} format`;
  }
  const reason = text(event, "reason");
  if (reason) {
    return reason;
  }
  const outcome = text(event, "outcome") ?? text(event, "decision");
  return outcome ? `Outcome: ${outcome.replaceAll(/[-_]/g, " ")}` : undefined;
}
